import type Bottleneck from 'bottleneck';

import config from '../utils/config';

const reflect = (promise: Promise<any>): Promise<any> => {
    return promise.then(
        (v) => ({ v: v, status: 'fulfilled' }),
        (e) => ({ e: e, status: 'rejected' }),
    );
};

// The singleton lives in transport/core, which is in every route's first-load JS, while only a
// few endpoints are rate limited. Load bottleneck (~16 KB gzip) with the first scheduled request.
type BottleneckClass = typeof Bottleneck;
let bottleneckLoad: Promise<BottleneckClass> | undefined;

function loadBottleneck(): Promise<BottleneckClass> {
    bottleneckLoad ??= import('bottleneck').then(
        (mod) => mod.default,
        (error: unknown) => {
            // Forget the failure so the next request retries the load.
            bottleneckLoad = undefined;
            throw error;
        },
    );
    return bottleneckLoad;
}

export default class RateLimiter {
    requestQueues: Map<string, Bottleneck>;
    // Bumped by clear(). A request scheduled before a clear() whose bottleneck load finishes
    // after it is dropped, as bottleneck drops the jobs still waiting in a stopped limiter.
    private generation = 0;

    constructor() {
        this.requestQueues = new Map();
    }

    schedule<T>(key: string, job: () => PromiseLike<T>): Promise<T> {
        const generation = this.generation;
        return loadBottleneck().then((BottleneckCtor) => {
            if (generation !== this.generation) {
                throw new BottleneckCtor.BottleneckError('This limiter has been stopped.');
            }
            return this.getLimiter(BottleneckCtor, key).schedule(job);
        });
    }

    private getLimiter(BottleneckCtor: BottleneckClass, key: string): Bottleneck {
        let limiter = this.requestQueues.get(key);
        if (!!limiter) {
            return limiter;
        }
        limiter = new BottleneckCtor({
            minTime: config.minTimeBetweenRequestsMs,
            maxConcurrent: config.maxConcurrentRequests,
        });
        this.requestQueues.set(key, limiter);
        return limiter;
    }

    // Canceling all the request promises
    clear(unlockCallback: () => void): void {
        this.generation += 1;
        const promises: Array<Promise<void>> = [];
        for (const [, limiter] of this.requestQueues) {
            promises.push(limiter.stop());
        }
        Promise.all(promises.map((p) => reflect(p))).then(() => {
            this.requestQueues.clear();
            unlockCallback();
        });
    }
}
